import moment from "moment";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { useTickets } from "./TicketContext";
import TicketService from "../../../services/ticketing/TicketingServices";
import { getTrans as t } from "../../../Localization/translations";
import Tooltip from "@tippyjs/react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const statusOptions = ["open", "resolved", "closed", "escalated", "unassigned"];

const getStatusClass = (status) => {
  switch (status) {
    case "new":
      return "badge bg-info text-dark";
    case "open":
      return "badge bg-success";
    case "closed":
      return "badge bg-danger";
    case "resolved":
      return "badge bg-success";
    case "escalated":
      return "badge bg-danger";
    case "replied":
      return "badge bg-warning text-dark";
    case "unassigned":
      return "badge bg-secondary";
    default:
      return "badge bg-secondary";
  }
};

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: "24px",
    height: "24px",
    fontSize: "0.85rem",
    border: "none",
    boxShadow: "none",
    backgroundColor: "transparent",
    display: "none",
  }),
  menu: (provided) => ({
    ...provided,
    width: "150px",
    marginTop: "2px",
    zIndex: 1000,
    borderRadius: "4px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  }),
  option: (provided, state) => ({
    ...provided,
    padding: "5px 10px",
    fontSize: "0.85rem",
    cursor: "pointer",
    backgroundColor: state.isSelected
      ? "#007bff"
      : state.isFocused
        ? "#e9ecef"
        : "white",
    color: state.isSelected ? "white" : "#333",
  }),
  singleValue: (provided) => ({
    ...provided,
    display: "none",
  }),
};

export default function EditCompanyTicket() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ticketId = searchParams.get("ticket_id");
  const { tickets, actions } = useTickets();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("open");
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableFields, setEditableFields] = useState({
    title: "",
    description: "",
    category: "",
    sub_category: "",
    priority: "",
    assigned_to: "",
    due_date: "",
    internal_ticket: "",
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [permissions, setPermissions] = useState(
    localStorage.getItem("permissions")
  );
  const hasViewAllTicketsPermission =
    permissions && permissions.split(",").includes("view_all_tickets");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;
  const [currentCommentPage, setCurrentCommentPage] = useState(1);
  const COMMENTS_PER_PAGE = 3;

  const loadTicketData = useCallback(async () => {
    if (hasLoadedOnce) return;

    try {
      setLoading(true);
      setError(null);
      setHasLoadedOnce(true);

      const response = await TicketService.getTicketById(ticketId);
      let ticketData = response.data?.data || response.data;

      if (ticketData) {
        setTicket(ticketData);
        setAttachments(ticketData.attachments || []);
        setStatus(ticketData.status || "open");
        setEditableFields({
          title: ticketData.title || "",
          description: ticketData.description || "",
          category: ticketData.category || "",
          sub_category: ticketData.sub_category || "",
          priority: ticketData.priority || "low",
          assigned_to: ticketData.assigned_to || "",
          due_date: ticketData.due_date || "",
          internal_ticket: ticketData.internal_ticket || "",
        });

        const commentsResponse = await TicketService.getTicketComments(
          ticketData.ticket_id
        );
        const commentsData =
          commentsResponse?.data?.data || commentsResponse?.data || [];
        setComments(commentsData);

        await loadDropdownData(ticketData.category_id);
      } else {
        setError("Ticket not found");
      }
    } catch (err) {
      setError(err.message || "Failed to load ticket");
    } finally {
      setLoading(false);
    }
  }, [ticketId, hasLoadedOnce]);

  useEffect(() => {
    if (ticketId && !hasLoadedOnce) {
      loadTicketData();
    }
  }, [ticketId, hasLoadedOnce, loadTicketData]);

  const loadDropdownData = async (currentCategoryId = null) => {
    try {
      const categoriesResponse = await TicketService.getCategories();
      const categoriesData =
        categoriesResponse?.data?.data || categoriesResponse?.data || [];
      setCategories(categoriesData);

      // Fetch subcategories for either the ticket's category or the first category
      const categoryToFetch =
        currentCategoryId ||
        (categoriesData.length > 0 ? categoriesData[0].id : null);
      if (categoryToFetch) {
        const subcategoriesResponse = await TicketService.getSubcategories(
          categoryToFetch
        );
        const subcategoriesData =
          subcategoriesResponse?.data?.data ||
          subcategoriesResponse?.data ||
          [];
        setSubcategories(subcategoriesData);
      } else {
        setSubcategories([]); // No categories available, ensure subcategories is empty
      }

      const usersResponse = await actions.getAssignableUsers();
      const usersData = usersResponse?.data || [];
      const formattedUsers = usersData.map((user) => ({
        id: user.user,
        name: user.name,
        email: user.user_email,
      }));
      console.log("Assignable users in EditCompanyTicket:", formattedUsers);
      setUsers(formattedUsers);
    } catch (err) {
      console.error("Error loading dropdown data:", err);
      setUsers([]);
      setCategories([]);
      setSubcategories([]);
    }
  };

  const handleCategoryChange = async (selectedCategory) => {
    setEditableFields((prev) => ({
      ...prev,
      category: selectedCategory ? selectedCategory.value : "",
      sub_category: "",
    }));

    if (selectedCategory) {
      try {
        const subcategoriesResponse = await TicketService.getSubcategories(
          selectedCategory.value
        );
        const subcategoriesData =
          subcategoriesResponse?.data?.data ||
          subcategoriesResponse?.data ||
          [];
        setSubcategories(subcategoriesData);
      } catch (err) {
        console.error("Error loading subcategories:", err);
        setSubcategories([]);
      }
    } else {
      setSubcategories([]);
    }
  };

  const handleUpdateTicket = async () => {
    if (!ticket) return;

    setIsUpdating(true);
    try {
      const updateData = {
        title: editableFields.title || undefined,
        description: editableFields.description || undefined,
        category: editableFields.category || undefined,
        sub_category: editableFields.sub_category || undefined,
        priority: editableFields.priority,
        assigned_to: editableFields.assigned_to || undefined,
        due_date: editableFields.due_date
          ? new Date(editableFields.due_date).toISOString().split("T")[0]
          : undefined,
        internal_ticket: editableFields.internal_ticket || undefined,
      };
      if (
        updateData.assigned_to &&
        (!ticket.assigned_to || ticket.status === "new")
      ) {
        updateData.status = "open";
      }

      Object.keys(updateData).forEach(
        (key) => updateData[key] === undefined && delete updateData[key]
      );

      await TicketService.updateTicket(ticket.ticket_id, updateData);

      setTicket((prev) => ({
        ...prev,
        title: editableFields.title,
        description: editableFields.description,
        category: editableFields.category,
        sub_category: editableFields.sub_category,
        priority: editableFields.priority,
        assigned_to: editableFields.assigned_to,
        due_date: editableFields.due_date,
        category_name:
          categories.find((c) => c.id === editableFields.category)?.name ||
          prev.category_name,
        sub_category_name:
          subcategories.find((sc) => sc.id === editableFields.sub_category)
            ?.name || prev.sub_category_name,
        assigned_to_name:
          users.find((u) => u.id === editableFields.assigned_to)?.name ||
          prev.assigned_to_name,
        assigned_to_email:
          users.find((u) => u.id === editableFields.assigned_to)?.email ||
          prev.assigned_to_email,
        status: updateData.status || prev.status,
        status_display: updateData.status
          ? updateData.status.charAt(0).toUpperCase() +
          updateData.status.slice(1)
          : prev.status_display,
        internal_ticket: updateData.internal_ticket || prev.internal_ticket,
      }));

      setIsEditMode(false);
      toast.success("Ticket updated successfully!", {
        position: "top-right",
        autoClose: 2000,
      });
      await actions.fetchTickets();
    } catch (err) {
      console.error("Error updating ticket:", err);
      toast.error(
        "Failed to update ticket: " +
        (err.response?.data?.message || err.message),
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUploadAttachment = async () => {
    if (!selectedFile || !ticket) return;

    try {
      const uploadResponse = await TicketService.uploadAttachment(
        ticket.ticket_id,
        selectedFile
      );
      const uploadedAttachment =
        uploadResponse?.data?.data || uploadResponse?.data;

      setAttachments((prev) =>
        Array.isArray(prev)
          ? [...prev, uploadedAttachment]
          : [uploadedAttachment]
      );

      setSelectedFile(null);
      setCurrentPage(1);
      toast.success("Attachment uploaded successfully!", {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (err) {
      console.error("Error uploading attachment:", err);
      toast.error(
        "Failed to upload attachment: " +
        (err.response?.data?.message || err.message),
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!ticket) return;

    try {
      switch (newStatus) {
        case "escalated":
          await TicketService.escalateTicket(ticket.ticket_id);
          toast.success("Ticket escalated successfully", {
            position: "top-right",
            autoClose: 2000,
          });
          setTimeout(() => navigate("/companyticket"), 2000);
          break;
        case "resolved":
          await TicketService.resolveTicket(ticket.ticket_id);
          toast.success("Ticket resolved successfully", {
            position: "top-right",
            autoClose: 2000,
          });
          setTimeout(() => navigate("/companyticket"), 2000);
          break;
        case "closed":
          await TicketService.closeTicket(ticket.ticket_id);
          toast.success("Ticket closed successfully", {
            position: "top-right",
            autoClose: 2000,
          });
          setTimeout(() => navigate("/companyticket"), 2000);
          break;
        case "unassigned":
          const unassignData = {
            status: "unassigned",
            assigned_to: null,
          };
          await TicketService.updateTicket(ticket.ticket_id, unassignData);
          toast.success("Ticket unassigned successfully", {
            position: "top-right",
            autoClose: 2000,
          });
          setTimeout(() => navigate("/companyticket"), 2000);
          break;
        default:
          const updateData = { status: newStatus };
          await TicketService.updateTicket(ticket.ticket_id, updateData);
          toast.success(`Ticket ${newStatus} successfully!`, {
            position: "top-right",
            autoClose: 2000,
          });
          setTimeout(() => navigate("/companyticket"), 2000);
          break;
      }

      setTicket((prev) => ({
        ...prev,
        status: newStatus,
        status_display:
          newStatus === "unassigned"
            ? "Unassigned"
            : newStatus.charAt(0).toUpperCase() + newStatus.slice(1),
        is_escalated: newStatus === "escalated" ? true : prev.is_escalated,
        assigned_to: newStatus === "unassigned" ? null : prev.assigned_to,
        assigned_to_name:
          newStatus === "unassigned" ? "" : prev.assigned_to_name,
        assigned_to_email:
          newStatus === "unassigned" ? "" : prev.assigned_to_email,
      }));

      if (newStatus === "unassigned") {
        setEditableFields((prev) => ({
          ...prev,
          assigned_to: "",
        }));
      }

      await actions.fetchTickets();
    } catch (err) {
      setError(`Failed to update ticket to ${newStatus}: ` + err.message);
      toast.error(`Failed to update ticket to ${newStatus}: ` + err.message, {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !ticket) return;

    try {
      const commentData = {
        content: newComment,
        is_internal: true,
      };
      const addCommentResponse = await TicketService.addTicketComment(
        ticket.ticket_id,
        commentData
      );
      await new Promise((resolve) => setTimeout(resolve, 1500));
      const commentsResponse = await TicketService.getTicketComments(
        ticket.ticket_id
      );
      let commentsData =
        commentsResponse?.data?.data || commentsResponse?.data || [];
      setComments(commentsData);
      setNewComment("");
      setError(null);
      toast.success("Comment added successfully", {
        position: "top-right",
        autoClose: 1000,
      });
    } catch (err) {
      console.error("Company - Error adding comment:", err);
      setError("Failed to add comment: " + err.message);
      toast.error("Failed to add comment: " + err.message, {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const totalCommentPages = Math.ceil(comments.length / COMMENTS_PER_PAGE);
  const startCommentIndex = (currentCommentPage - 1) * COMMENTS_PER_PAGE;
  const paginatedComments = comments.slice(
    startCommentIndex,
    startCommentIndex + COMMENTS_PER_PAGE
  );

  const totalPages = Math.ceil(attachments.length / itemsPerPage);
  const currentAttachments = attachments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePrevious = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handleDownload = (fileUrl) => {
    if (fileUrl) {
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    } else {
      toast.error("No download link available", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const priorityOptions = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "critical", label: "Critical" },
  ];

  if (loading) {
    return (
      <div className="bg-light min-vh-100 d-flex justify-content-center align-items-center">
        <div className="text-center">
          <div className="spinner-border text-secondary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading ticket details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-light min-vh-100 d-flex justify-content-center align-items-center">
        <div className="text-center">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Error!</h4>
            <p>{error}</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate("/companyticket")}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="bg-light min-vh-100 d-flex justify-content-center align-items-center">
        <div className="text-center">
          <div className="alert alert-warning" role="alert">
            <h4 className="alert-heading">Ticket Not Found</h4>
            <p>The requested ticket could not be found.</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate("/companyticket")}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid bg-light min-vh-100 p-4">
      <main>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4>Ticket {ticket.ticket_id}</h4>
            {/* <div className="position-relative">
              <span
                className={`badge ${getStatusClass(status)} cursor-pointer`}
                onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              >
                {ticket.status_display || status.charAt(0).toUpperCase() + status.slice(1)}
              </span> */}
            {/* {isStatusDropdownOpen && (
                <div className="status-dropdown dropdown-arrow.roated">
                  <Select
                    options={statusOptions.map(option => ({
                      value: option,
                      label: option === 'unassigned' ? 'Unassigned' : option.charAt(0).toUpperCase() + option.slice(1)
                    }))}
                    value={{
                      value: status,
                      label: status === 'unassigned' ? 'Unassigned' : status.charAt(0).toUpperCase() + status.slice(1)
                    }}
                    onChange={(selected) => {
                      setStatus(selected.value);
                      handleStatusChange(selected.value);
                      setIsStatusDropdownOpen(false);
                    }}
                    styles={customSelectStyles}
                    menuIsOpen={true}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>
              )} */}
            <div className="position-relative">
              <span
                className={`badge ${getStatusClass(status)} cursor-pointer `}
                onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  setIsStatusDropdownOpen(!isStatusDropdownOpen)
                }
                style={{ paddingRight: "8px" }} // Extra space for arrow
              >
                <span>
                  {ticket.status_display ||
                    status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`dropdown-arrow ${isStatusDropdownOpen ? "rotated" : ""
                    }`}
                  style={{
                    transition: "transform 0.2s ease",
                    color: "currentColor", // Matches badge text color
                    flexShrink: 0, // Prevents shrinking
                  }}
                >
                  <path
                    d="M1 3L5 7L9 3"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              {isStatusDropdownOpen && (
                <div
                  className="status-dropdown"
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    zIndex: 1000,
                  }}
                >
                  <Select
                    options={statusOptions.map((option) => ({
                      value: option,
                      label:
                        option === "unassigned"
                          ? "Unassigned"
                          : option.charAt(0).toUpperCase() + option.slice(1),
                    }))}
                    value={{
                      value: status,
                      label:
                        status === "unassigned"
                          ? "Unassigned"
                          : status.charAt(0).toUpperCase() + status.slice(1),
                    }}
                    onChange={(selected) => {
                      setStatus(selected.value);
                      handleStatusChange(selected.value);
                      setIsStatusDropdownOpen(false);
                    }}
                    styles={customSelectStyles}
                    menuIsOpen={true}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>
              )}
              {/* </div> */}
            </div>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-primary mb-0 btn-sm"
              onClick={() => navigate("/companyticket")}
            >
              Back
            </button>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-8 col-md-12">
            <div className="card shadow-sm h-100">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">{t("Ticket_info")}</h5>
                  <button
                    onClick={() => setIsEditMode(!isEditMode)}
                    disabled={isUpdating}
                    title={isEditMode ? "Cancel Edit" : "Edit Ticket"}
                    style={{
                      border: "none",
                      background: "white", // white background
                      padding: "4px", // small space around image
                      borderRadius: "6px", // optional rounded corners
                      cursor: "pointer",
                    }}
                  >
                    <img src="/static/images/ic-edit.svg" alt="Edit ticket" />
                  </button>
                </div>
              </div>

              <div className="card-body">
                {isEditMode ? (
                  <>
                    <div className="row mb-3">
                      {/* Left column (Title + Priority) */}
                      <div className="col-md-6">
                        {/* Title */}
                        <span className="sub-title tc">
                          {t("Subject")}{" "}
                          <Tooltip content={t("Subject_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <input
                          type="text"
                          className="form-control mb-3"
                          value={editableFields.title}
                          onChange={(e) =>
                            setEditableFields((prev) => ({
                              ...prev,
                              title: e.target.value,
                            }))
                          }
                        />

                        {/* Priority */}
                        <span className="sub-title tc">
                          {t("priority")}{" "}
                          <Tooltip content={t("priority_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <Select
                          value={priorityOptions.find(
                            (p) => p.value === editableFields.priority
                          )}
                          options={priorityOptions}
                          onChange={(selected) =>
                            setEditableFields((prev) => ({
                              ...prev,
                              priority: selected ? selected.value : "low",
                            }))
                          }
                          placeholder="Select Priority"
                          className="react-select-container"
                          classNamePrefix="react-select"
                        />
                      </div>

                      {/* Right column (Description) */}
                      <div className="col-md-6">
                        <span className="sub-title tc">
                          {t("Description")}{" "}
                          <Tooltip content={t("description_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <textarea
                          className="form-control textareaticket"
                          style={{ height: "115px" }}
                          value={editableFields.description}
                          onChange={(e) =>
                            setEditableFields((prev) => ({
                              ...prev,
                              description: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>

                    {/* <div className="row">
                      <div className="col-md-6 mb-3">
                        <span className="sub-title tc">
                          {t("title")}{" "}
                          <Tooltip content={t("title_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          value={editableFields.title}
                          onChange={e =>
                            setEditableFields(prev => ({
                              ...prev,
                              title: e.target.value
                            }))
                          }
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <span className="sub-title tc">
                          {t("description")}{" "}
                          <Tooltip content={t("description_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <textarea
                          className="textareaticket"
                          rows="3"
                          cols="40"
                          // className="form-control"
                          value={editableFields.description}
                          onChange={e =>
                            setEditableFields(prev => ({
                              ...prev,
                              description: e.target.value
                            }))
                          }
                        />
                      </div>
                    </div> */}
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <span className="sub-title tc">
                          {t("category")}{" "}
                          <Tooltip content={t("category_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <Select
                          value={
                            categories.find(
                              (c) => c.id === editableFields.category
                            )
                              ? {
                                value: editableFields.category,
                                label: categories.find(
                                  (c) => c.id === editableFields.category
                                )?.name,
                              }
                              : null
                          }
                          options={categories.map((category) => ({
                            value: category.id,
                            label: category.name,
                          }))}
                          onChange={handleCategoryChange}
                          isClearable
                          placeholder="Select Category"
                          className="react-select-container"
                          classNamePrefix="react-select"
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <span className="sub-title tc">
                          {t("subcategory")}{" "}
                          <Tooltip content={t("subcategory_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <Select
                          value={
                            subcategories.find(
                              (sc) => sc.id === editableFields.sub_category
                            )
                              ? {
                                value: editableFields.sub_category,
                                label: subcategories.find(
                                  (sc) =>
                                    sc.id === editableFields.sub_category
                                )?.name,
                              }
                              : null
                          }
                          options={subcategories.map((subcategory) => ({
                            value: subcategory.id,
                            label: subcategory.name,
                          }))}
                          onChange={(selected) =>
                            setEditableFields((prev) => ({
                              ...prev,
                              sub_category: selected ? selected.value : "",
                            }))
                          }
                          isClearable
                          placeholder="Select Subcategory"
                          isDisabled={!editableFields.category}
                          className="react-select-container"
                          classNamePrefix="react-select"
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <span className="sub-title tc">
                          {t("created")}{" "}
                          <Tooltip content={t("created_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          value={
                            ticket.created_at
                              ? moment(ticket.created_at).format("MM/DD/YYYY")
                              : ""
                          }
                          disabled
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <span className="sub-title tc">
                          {t("due_date")}{" "}
                          <Tooltip content={t("due_date_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>

                        <DatePicker
                          className="form-control"
                          selected={
                            editableFields.due_date ? new Date(editableFields.due_date) : null
                          }
                          onChange={(date) =>
                            setEditableFields((prev) => ({
                              ...prev,
                              due_date: date,
                            }))
                          }
                          dateFormat="MM/dd/yyyy"
                          placeholderText="MM/DD/YYYY"
                          showYearDropdown
                          scrollableYearDropdown
                          showMonthDropdown
                          dropdownMode="select"
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <span className="sub-title tc">
                          {t("created_by")}{" "}
                          <Tooltip content={t("created_by_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          value={ticket.client_name}
                          disabled
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <span className="sub-title tc">
                          {t("company")}{" "}
                          <Tooltip content={t("company_Tooltip")}>
                            <i className="fa fa-light fa-circle-info"></i>
                          </Tooltip>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          value={ticket.company_name}
                          disabled
                        />
                      </div>
                    </div>
                    {hasViewAllTicketsPermission && (
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <span className="sub-title tc">
                            {t("assigned_to")}{" "}
                            <Tooltip content={t("assigned_to_Tooltip")}>
                              <i className="fa fa-light fa-circle-info"></i>
                            </Tooltip>
                          </span>
                          <Select
                            value={
                              users.find(
                                (u) => u.id === editableFields.assigned_to
                              )
                                ? {
                                  value: editableFields.assigned_to,
                                  label: `${users.find(
                                    (u) =>
                                      u.id === editableFields.assigned_to
                                  )?.name
                                    }`,
                                }
                                : null
                            }
                            options={users.map((user) => ({
                              value: user.id,
                              label: `${user.name}`,
                            }))}
                            onChange={(selected) =>
                              setEditableFields((prev) => ({
                                ...prev,
                                assigned_to: selected ? selected.value : "",
                              }))
                            }
                            isClearable
                            placeholder="Select User to Assign"
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <span className="sub-title tc">
                            {t("Internal Ticket")}{" "}
                            <Tooltip
                              content={t("Paste the full URL of ticket here")}
                            >
                              <i className="fa fa-light fa-circle-info"></i>
                            </Tooltip>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            value={editableFields.internal_ticket || ""}
                            onChange={(e) =>
                              setEditableFields((prev) => ({
                                ...prev,
                                internal_ticket: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                    )}

                    <div className="mb-3">
                      <button
                        className="btn btn-secondary me-2"
                        onClick={() => setIsEditMode(false)}
                        disabled={isUpdating}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleUpdateTicket}
                        disabled={isUpdating}
                      >
                        {isUpdating ? "Updating..." : "Update"}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="row">
                      <div
                        className="col-xl-6 d-flex"
                        style={{ justifyContent: "space-between" }}
                      >
                        <label className="col-form-label">
                          {t("Subject")}{" "}
                          <Tooltip content={t("Subject_Tooltip")}>
                            <i
                              className="fa fa-light fa-circle-info"
                              aria-hidden="true"
                            ></i>
                          </Tooltip>
                        </label>
                        <div
                          className="col-xl-6 d-flex"
                          style={{ justifyContent: "space-between" }}
                        >
                          <span className="view-label p">{ticket.title}</span>
                        </div>
                      </div>
                      <div
                        className="col-xl-6 d-flex"
                        style={{ justifyContent: "space-between" }}
                      >
                        <label className="col-form-label">
                          {t("Description")}{" "}
                          <Tooltip content={t("description_Tooltip")}>
                            <i
                              className="fa fa-light fa-circle-info"
                              aria-hidden="true"
                            ></i>
                          </Tooltip>
                        </label>
                        <div
                          className="col-xl-6 d-flex"
                          style={{ justifyContent: "space-between" }}
                        >
                          <span className="view-label p">
                            {ticket.description || "No description provided"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div
                        className="col-xl-6 d-flex"
                        style={{ justifyContent: "space-between" }}
                      >
                        <label className="col-form-label">
                          {t("priority")}{" "}
                          <Tooltip content={t("priority_Tooltip")}>
                            <i
                              className="fa fa-light fa-circle-info"
                              aria-hidden="true"
                            ></i>
                          </Tooltip>
                        </label>
                        <div
                          className="col-xl-6 d-flex"
                          style={{ justifyContent: "space-between" }}
                        >
                          <span className="view-label p">
                            {ticket.priority_display || ticket.priority}
                          </span>
                        </div>
                      </div>
                      <div
                        className="col-xl-6 d-flex"
                        style={{ justifyContent: "space-between" }}
                      >
                        <label className="col-form-label">
                          {t("category")}{" "}
                          <Tooltip content={t("category_Tooltip")}>
                            <i
                              className="fa fa-light fa-circle-info"
                              aria-hidden="true"
                            ></i>
                          </Tooltip>
                        </label>
                        <div
                          className="col-xl-6 d-flex"
                          style={{ justifyContent: "space-between" }}
                        >
                          <span className="view-label p">
                            {ticket.category_name}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div
                        className="col-xl-6 d-flex"
                        style={{ justifyContent: "space-between" }}
                      >
                        <label className="col-form-label">
                          {t("subcategory")}{" "}
                          <Tooltip content={t("subcategory_Tooltip")}>
                            <i
                              className="fa fa-light fa-circle-info"
                              aria-hidden="true"
                            ></i>
                          </Tooltip>
                        </label>
                        <div
                          className="col-xl-6 d-flex"
                          style={{ justifyContent: "space-between" }}
                        >
                          <span className="view-label p">
                            {ticket.sub_category_name}
                          </span>
                        </div>
                      </div>
                      <div
                        className="col-xl-6 d-flex"
                        style={{ justifyContent: "space-between" }}
                      >
                        <label className="col-form-label">
                          {t("company")}{" "}
                          <Tooltip content={t("company_Tooltip")}>
                            <i
                              className="fa fa-light fa-circle-info"
                              aria-hidden="true"
                            ></i>
                          </Tooltip>
                        </label>
                        <div
                          className="col-xl-6 d-flex"
                          style={{ justifyContent: "space-between" }}
                        >
                          <span className="view-label p">
                            {ticket.company_name}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      {hasViewAllTicketsPermission && (
                        <div
                          className="col-xl-6 d-flex"
                          style={{ justifyContent: "space-between" }}
                        >
                          <label className="col-form-label">
                            {t("assigned_to")}{" "}
                            <Tooltip content={t("assigned_to_Tooltip")}>
                              <i
                                className="fa fa-light fa-circle-info"
                                aria-hidden="true"
                              ></i>
                            </Tooltip>
                          </label>
                          <div
                            className="col-xl-6 d-flex"
                            style={{ justifyContent: "space-between" }}
                          >
                            <span className="view-label p">
                              {ticket.assigned_to_name || "Unassigned"}
                            </span>
                          </div>
                        </div>
                      )}
                      <div
                        className="col-xl-6 d-flex"
                        style={{ justifyContent: "space-between" }}
                      >
                        <label className="col-form-label">
                          {t("created_by")}{" "}
                          <Tooltip content={t("created_by_Tooltip")}>
                            <i
                              className="fa fa-light fa-circle-info"
                              aria-hidden="true"
                            ></i>
                          </Tooltip>
                        </label>
                        <div
                          className="col-xl-6 d-flex"
                          style={{ justifyContent: "space-between" }}
                        >
                          <span className="view-label p">
                            {ticket.client_name}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div
                        className="col-xl-6 d-flex"
                        style={{ justifyContent: "space-between" }}
                      >
                        <label className="col-form-label">
                          {t("created")}{" "}
                          <Tooltip content={t("created_Tooltip")}>
                            <i
                              className="fa fa-light fa-circle-info"
                              aria-hidden="true"
                            ></i>
                          </Tooltip>
                        </label>
                        <div
                          className="col-xl-6 d-flex"
                          style={{ justifyContent: "space-between" }}
                        >
                          <span className="view-label p">
                            {ticket.created_at ? moment(ticket.created_at).format("MM/DD/YYYY") : "None"}
                          </span>
                        </div>
                      </div>
                      <div
                        className="col-xl-6 d-flex"
                        style={{ justifyContent: "space-between" }}
                      >
                        <label className="col-form-label">
                          {t("due_date")}{" "}
                          <Tooltip content={t("due_date_Tooltip")}>
                            <i
                              className="fa fa-light fa-circle-info"
                              aria-hidden="true"
                            ></i>
                          </Tooltip>
                        </label>
                        <div
                          className="col-xl-6 d-flex"
                          style={{ justifyContent: "space-between" }}
                        >
                          <span className="view-label p">
                            {ticket.due_date ? moment(ticket.due_date).format("MM/DD/YYYY") : "None"}
                          </span>
                        </div>
                      </div>
                    </div>
                    {hasViewAllTicketsPermission && (
                      <div className="row">
                        <div
                          className="col-xl-6 d-flex"
                          style={{ justifyContent: "space-between" }}
                        >
                          <label className="col-form-label">
                            {t("Internal Ticket")}{" "}
                            <Tooltip
                              content={t("Paste the full URL of ticket here")}
                            >
                              <i
                                className="fa fa-light fa-circle-info"
                                aria-hidden="true"
                              ></i>
                            </Tooltip>
                          </label>
                          <div
                            className="col-xl-6 d-flex"
                            style={{ justifyContent: "space-between" }}
                          >
                            {ticket.internal_ticket ? (
                              <a
                                href={ticket.internal_ticket}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="view-label p"
                                style={{
                                  color: "#007bff",
                                  textDecoration: "none",
                                }}
                              >
                                {ticket.internal_ticket.match(
                                  /\/(\d+)-/
                                )?.[1] || ticket.internal_ticket}
                              </a>
                            ) : (
                              <span className="view-label p">-</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    <hr className="my-4" />
                    <div className="col-12">
                      <div>
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">
                              {t("comments")} ({comments.length})
                            </h5>
                          </div>
                          <div className="mb-3">
                            <textarea
                              className="form-control"
                              rows="3"
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              placeholder={t("Type your comment here...")}
                            ></textarea>
                            <div className="d-flex justify-content-end">
                              <button
                                className="btn btn-primary btn-sm mt-2"
                                onClick={handleAddComment}
                                disabled={!newComment.trim()}
                              >
                                Add Comment
                              </button>
                            </div>
                          </div>
                          {comments.length === 0 ? (
                            <p className="text-muted">No comments yet.</p>
                          ) : (
                            <div
                              className="comments-list"
                              style={{
                                maxHeight: "300px",
                                overflowY: "auto",
                                padding: "0.5rem",
                                backgroundColor: "var(--bs-table-bg)",
                                borderBottomWidth: "1px",
                                boxShadow:
                                  "inset 0 0 0 9999px var(--bs-table-accent-bg)",
                                position: "relative",
                              }}
                              aria-label="Comments list with scrollbar"
                            >
                              {[...comments].reverse().map((comment, index) => (
                                <div
                                  key={comment.id || index}
                                  className="border-bottom pb-2 mb-2"
                                >
                                  <div className="d-flex justify-content-between">
                                    <small>
                                      {comment.client_name ||
                                        (comment.is_internal
                                          ? "Company"
                                          : "Customer")}
                                    </small>
                                    <small className="text-muted">
                                      {moment(comment.created_at).format("MM/DD/YYYY hh:mm A")}
                                    </small>
                                  </div>
                                  <p className="mb-0">
                                    {comment.content ||
                                      comment.comment ||
                                      comment.message ||
                                      "No content"}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-4 col-md-12">
            <div className="card shadow-sm h-200">
              {/* <div className="card-header">
                <h5 className="mb-0">{t("")}</h5>
              </div> */}
              <div className="card-body">
                <div className="mb-4">
                  <label className="col-form-label">
                    {t("Upload Attachment")}{" "}
                    <Tooltip content={t("upload_attachment_Tooltip")}>
                      <i className="fa fa-light fa-circle-info"></i>
                    </Tooltip>
                  </label>
                  <input
                    type="file"
                    className="form-control mb-2"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                  />
                  <div className="d-flex justify-content-end">
                    <button
                      className="btn btn-primary btn-sm "
                      onClick={handleUploadAttachment}
                      disabled={!selectedFile || isUpdating}
                    >
                      UPLOAD
                    </button>
                  </div>
                </div>
                <div className="col-12">
                  <div>
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">
                          Documents ({attachments.length})
                        </h5>
                      </div>

                      <div
                        className="documents-list p-2"
                        style={{
                          maxHeight: "500px",
                          overflowX: "hidden",
                          overflowY: "auto",
                        }}
                      >
                        {attachments.length > 0 ? (
                          <>
                            {attachments.map((doc) => {
                              const fileName =
                                doc.original_filename || doc.filename || "";
                              const extension =
                                fileName.split(".").pop()?.toLowerCase() || "";
                              return (
                                <div
                                  className="document-item d-flex align-items-center mb-2"
                                  key={doc.id}
                                  style={{ flexWrap: "nowrap" }}
                                >
                                  <div className="d-flex align-items-center flex-grow-1 me-2">
                                    <div className="me-2 fs-5">
                                      {extension === "pdf"
                                        ? "📄"
                                        : ["doc", "docx"].includes(extension)
                                          ? "📁"
                                          : ["jpg", "jpeg", "png"].includes(
                                            extension
                                          )
                                            ? "🖼️"
                                            : "🖼️"}
                                    </div>
                                    <div
                                      className="text-truncate"
                                      style={{ maxWidth: "200px" }}
                                    >
                                      <div className="fw-semibold small">
                                        {fileName}
                                      </div>
                                      <div className="text-muted small">
                                        {moment(doc.created_at || doc.uploaded_at).format("MM/DD/YYYY hh:mm A")}
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    className="btn btn-primary btn-sm"
                                    style={{
                                      minWidth: "80px",
                                      whiteSpace: "nowrap",
                                    }}
                                    onClick={() => {
                                      if (doc.file || doc.url) {
                                        window.open(
                                          doc.file || doc.url,
                                          "_blank",
                                          "noopener,noreferrer"
                                        );
                                      } else {
                                        toast.error(
                                          "No download link available",
                                          {
                                            position: "top-right",
                                            autoClose: 3000,
                                          }
                                        );
                                      }
                                    }}
                                  >
                                    Download
                                  </button>
                                </div>
                              );
                            })}
                          </>
                        ) : (
                          <div className="text-center py-4">
                            <p className="text-muted">No documents available</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <ToastContainer />
    </div>
  );
}
